import React, { useState, useEffect, useRef } from 'react';
import { Tooltip } from '../Tooltip';
import type { QueueDiagnostics } from './types';

// ==========================================
// CENTRAL HELPER
// ==========================================
// Using the same fetch helper pattern as legacy
const adminFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('X_ADMIN_TOKEN') || '';
    const headers = new Headers(options.headers || {});
    if (token) headers.set('X-Admin-Token', token.trim());

    if (options.body && typeof options.body === 'string' && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    return await fetch(url, { ...options, headers });
};

const formatError = async (r: Response) => {
    let text = '';
    try {
        text = await r.text();
    } catch {
        return `(${r.status}) [Body Read Failed]`;
    }

    try {
        const j = JSON.parse(text);
        return `(${r.status}) ${j.error || j.message || 'Error'}`;
    } catch {
        return `(${r.status}) ${text.slice(0, 100)}`;
    }
};

// ==========================================
// COMPONENT: ZIP IMPORT
// ==========================================
const BatchZipImport: React.FC = () => {
    const [bookId, setBookId] = useState('');
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [overwrite, setOverwrite] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setError('');
        }
    };

    const handleUpload = async () => {
        if (!bookId) { setError("Book ID is required"); return; }
        if (!file) { setError("No ZIP file selected"); return; }
        if (!file.name.toLowerCase().endsWith('.zip')) { setError("File must be .zip"); return; }

        setUploading(true);
        setResult(null);
        setError('');

        const formData = new FormData();
        formData.append("bookId", bookId);
        if (title) formData.append("title", title);
        if (author) formData.append("author", author);
        formData.append("overwrite", overwrite.toString());
        formData.append("zip", file);

        try {
            // Need custom fetch for FormData to let browser set Content-Type + boundary
            const token = localStorage.getItem('X_ADMIN_TOKEN') || '';
            const headers: any = {};
            if (token) headers['X-Admin-Token'] = token.trim();
            // Do NOT set Content-Type here!

            const res = await fetch('/api/library/audio/import_zip', {
                method: 'POST',
                headers,
                body: formData
            });

            if (res.ok) {
                const json = await res.json();
                setResult(json);
            } else {
                setError(await formatError(res));
            }

        } catch (e) {
            setError(String(e));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', color: '#eee' }}>
            <div style={{ marginBottom: '20px', background: '#222', padding: '15px', borderRadius: '4px' }}>
                <h3 style={{ marginTop: 0 }}>Import Audio ZIP</h3>
                <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '15px' }}>
                    Upload a ZIP containing numbered MP3 files (e.g. <code>0.mp3</code>, <code>1.mp3</code>).
                    <br />
                    Files will be normalized and uploaded to R2. Playlist will be generated automatically.
                    <br />
                    <strong>Note:</strong> <code>.wav</code> files are NOT supported. Convert to MP3 locally.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>BOOK ID (Required)</label>
                        <input
                            type="text" value={bookId} onChange={e => setBookId(e.target.value)}
                            placeholder="my-audiobook-id"
                            style={{ width: '100%', padding: '8px', background: '#333', border: '1px solid #555', color: '#fff' }}
                        />
                    </div>
                    <div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>TITLE (Optional)</label>
                                <input
                                    type="text" value={title} onChange={e => setTitle(e.target.value)}
                                    placeholder="The Great Book"
                                    style={{ width: '100%', padding: '8px', background: '#333', border: '1px solid #555', color: '#fff' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>AUTHOR (Optional)</label>
                                <input
                                    type="text" value={author} onChange={e => setAuthor(e.target.value)}
                                    placeholder="Jane Doe"
                                    style={{ width: '100%', padding: '8px', background: '#333', border: '1px solid #555', color: '#fff' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>ZIP FILE</label>
                        <input type="file" accept=".zip" onChange={handleFileChange} style={{ fontSize: '13px' }} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                        <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
                        Overwrite existing files
                    </label>
                </div>

                <button
                    onClick={handleUpload}
                    disabled={uploading}
                    style={{
                        padding: '10px 20px',
                        background: uploading ? '#555' : '#2e7d32',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: uploading ? 'default' : 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    {uploading ? 'UPLOADING...' : 'UPLOAD & AUDIT'}
                </button>

                {error && <div style={{ marginTop: '15px', color: '#ef5350', fontWeight: 'bold' }}>ERROR: {error}</div>}
            </div>

            {result && (
                <div style={{ background: '#1a1a1a', padding: '15px', borderRadius: '4px', border: '1px solid #333' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <strong style={{ color: '#66bb6a' }}>SUCCESS</strong>
                        <span style={{ fontSize: '12px', color: '#888' }}>
                            Chunks: {result.uploadedChunks?.length || 0} |
                            Playlist: {result.playlistWritten ? 'Yes' : 'No'} |
                            Manifest: {result.manifestUpdated ? 'Updated' : 'No Change'}
                        </span>
                    </div>

                    {result.warnings?.length > 0 && (
                        <div style={{ marginBottom: '10px', color: '#ffa726', fontSize: '12px' }}>
                            <strong>Warnings:</strong>
                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                                {result.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                            </ul>
                        </div>
                    )}

                    {result.errors?.length > 0 && (
                        <div style={{ marginBottom: '10px', color: '#ef5350', fontSize: '12px' }}>
                            <strong>File Errors:</strong>
                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                                {result.errors.map((e: any, i: number) => <li key={i}>{e.filename}: {e.error}</li>)}
                            </ul>
                        </div>
                    )}

                    <div style={{ fontSize: '11px', color: '#888' }}>
                        <details>
                            <summary style={{ cursor: 'pointer' }}>Raw Result JSON</summary>
                            <pre style={{ margin: '10px 0', padding: '10px', background: '#111', overflowX: 'auto' }}>
                                {JSON.stringify(result, null, 2)}
                            </pre>
                        </details>
                    </div>
                </div>
            )}
        </div>
    );
};


// ==========================================
// COMPONENT: CSV IMPORT (LEGACY)
// ==========================================
// Copied from previous version
const BatchCsvImport: React.FC<{ diagnostics: QueueDiagnostics | null }> = ({ diagnostics }) => {
    // Check Stale Logic
    const isStale = diagnostics && diagnostics.consumerHealth > 300;

    // -------------------------------------------------------------------------
    // STATE: CSV & Parsing
    // -------------------------------------------------------------------------
    const [csv, setCsv] = useState('id,sourceUrl,title,author,voice,coverPrompt\n');
    const [parsedRows, setParsedRows] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [csvCollapsed, setCsvCollapsed] = useState(false);

    // -------------------------------------------------------------------------
    // STATE: Row Detail
    // -------------------------------------------------------------------------
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState<any>(null);
    const [status, setStatus] = useState('');
    const [lastError, setLastError] = useState('');

    // Metadata Edits
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [voice, setVoice] = useState('onyx');

    // Offsets
    const [startOffset, setStartOffset] = useState(0);
    const [endOffset, setEndOffset] = useState(0);
    const [trimMode, setTrimMode] = useState<'start' | 'end'>('start');

    // Portal / Viewport
    const [textTab, setTextTab] = useState<'head' | 'tail'>('head');
    const [previewWindowStart, setPreviewWindowStart] = useState<string>('');
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    // Cover
    const [coverPrompt, setCoverPrompt] = useState('');
    const [coverImg, setCoverImg] = useState<string | null>(null);
    const [coverLoading, setCoverLoading] = useState(false);
    const [coverStyleIdx, setCoverStyleIdx] = useState(0);

    // -------------------------------------------------------------------------
    // EFFECT: Parse CSV
    // -------------------------------------------------------------------------
    // -------------------------------------------------------------------------
    // HELPER: RFC4180 Parser
    // -------------------------------------------------------------------------
    const parseCSVLine = (line: string, delimiter: string): string[] => {
        const values: string[] = [];
        let curVal = '';
        let inQuote = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (inQuote) {
                if (char === '"' && nextChar === '"') {
                    curVal += '"';
                    i++; // Skip escaped quote
                } else if (char === '"') {
                    inQuote = false;
                } else {
                    curVal += char;
                }
            } else {
                if (char === '"') {
                    inQuote = true;
                } else if (char === delimiter) {
                    values.push(curVal.trim());
                    curVal = '';
                } else {
                    curVal += char;
                }
            }
        }
        values.push(curVal.trim());
        return values;
    };

    // -------------------------------------------------------------------------
    // EFFECT: Parse CSV
    // -------------------------------------------------------------------------
    const [counts, setCounts] = useState({ ready: 0, blocked: 0 });

    useEffect(() => {
        const lines = csv.split('\n').map(l => l.trim()).filter(l => l);
        if (lines.length === 0) { setParsedRows([]); setCounts({ ready: 0, blocked: 0 }); return; }

        let delim = ',';
        if (lines[0].includes('\t')) delim = '\t';
        else if (lines[0].includes(';')) delim = ';';

        // Parse Headers
        const rawHeaders = parseCSVLine(lines[0], delim);
        const headers = rawHeaders.map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());

        // Normalize headers
        const normHeaders = headers.map(h => {
            const low = h.trim().toLowerCase();
            if (low === 'sourceurl' || low === 'url' || low === 'link' || low === 'source') return 'sourceUrl';
            if (low === 'id' || low === 'slug') return 'id';
            if (low === 'coverprompt' || low === 'prompt') return 'coverPrompt';
            if (low === 'ttsmodel' || low === 'model') return 'ttsModel';
            if (low === 'instructionsprofile' || low === 'profile') return 'instructionsProfile';
            if (low === 'narratorgender' || low === 'gender') return 'narratorGender';
            return low;
        });

        let rCount = 0;
        let bCount = 0;

        const rows = lines.slice(1).map((l, i) => {
            const cols = parseCSVLine(l, delim);
            let obj: any = {};
            let errors: string[] = [];

            // 1. Strict Shape Check
            if (cols.length !== headers.length) {
                errors.push(`Shape Mismatch: Expected ${headers.length} cols, got ${cols.length}`);
            } else {
                normHeaders.forEach((h, idx) => obj[h] = cols[idx]);
            }

            // 2. Required Fields
            if (!obj.id || (!obj.sourceUrl && !obj.text)) errors.push("Missing ID or Source");

            // 3. Voice Validation & Normalization
            const VALID_VOICES = ['onyx', 'alloy', 'echo', 'fable', 'nova', 'shimmer', 'cedar', 'marin', 'custom'];
            const rawModel = obj.ttsModel || obj.ttsmodel;
            const rawGender = obj.narratorGender || obj.narratorgender;

            if (obj.voice) {
                if (!VALID_VOICES.includes(obj.voice)) {
                    obj._originalVoice = obj.voice;
                    if (rawModel === 'gpt-4o-mini-tts') {
                        obj.voice = rawGender === 'female' ? 'marin' : 'cedar';
                    } else {
                        obj.voice = 'onyx'; // Default SAFE
                    }
                    obj._voiceDefaulted = true;
                }
            } else {
                if (rawModel === 'gpt-4o-mini-tts') {
                    obj.voice = rawGender === 'female' ? 'marin' : 'cedar';
                } else {
                    obj.voice = 'onyx';
                }
                obj._voiceDefaulted = true;
            }

            // Sanitize ID
            if (obj.id) obj.id = obj.id.replace(/\.webp$|\.png$|\.jpg$/i, '');

            const isBlocked = errors.length > 0;
            if (isBlocked) bCount++; else rCount++;

            return { _i: i, ...obj, _errors: errors, _blocked: isBlocked };
        });

        setParsedRows(rows);
        setCounts({ ready: rCount, blocked: bCount });

        // Auto-select first row if unselected
        if (rows.length > 0 && parsedRows.length === 0) {
            setCurrentIndex(0);
        }
    }, [csv]);

    const handleCopyTSV = () => {
        if (parsedRows.length === 0) return;
        const h = ['id', 'sourceUrl', 'title', 'author', 'voice'];
        const r = parsedRows.map(row => {
            return h.map(k => row[k] || '').join('\t');
        }).join('\n');
        navigator.clipboard.writeText(h.join('\t') + '\n' + r);
        alert("Copied normalized TSV to clipboard!");
    };


    // -------------------------------------------------------------------------
    // EFFECT: Load Row
    // -------------------------------------------------------------------------
    const currentRow = parsedRows[currentIndex];
    const setStatusW = (s: string, isErr = false) => { setStatus(s); if (isErr) setLastError(s); };

    useEffect(() => {
        if (!currentRow) return;

        // Reset State
        setPreview(null);
        setCoverImg(null);
        setCoverPrompt('');
        setTitle('');
        setAuthor('');
        setVoice('onyx');
        setStartOffset(0);
        setEndOffset(0);
        setLastError('');
        setStatusW('Row changed. Loading...');

        loadRow(currentRow);
    }, [currentIndex, currentRow?.sourceUrl]);

    const reloadPreviewWindow = () => {
        if (!currentRow) return;
        let start = undefined;
        if (previewWindowStart) {
            const s = parseInt(previewWindowStart);
            if (!isNaN(s)) start = s;
        }
        loadRow(currentRow, start, true); // true = skipCover
    };

    const loadRow = async (row: any, windowStart?: number, skipCover?: boolean) => {
        if (!row || (!row.sourceUrl && !row.id)) {
            setStatusW('No sourceUrl or ID provided.', true);
            return;
        }

        setLoading(true);
        try {
            const previewBody: any = {
                sourceUrl: row.sourceUrl,
                id: row.id,
                title: row.title,
                author: row.author
            };
            if (windowStart !== undefined) previewBody.windowStart = windowStart;

            const r = await adminFetch('/api/factory/preview', {
                method: 'POST',
                body: JSON.stringify(previewBody)
            });

            if (r.ok) {
                const d = await r.json();
                setPreview(d);
                if (d.textHeadStart !== undefined) setPreviewWindowStart(String(d.textHeadStart));

                // Metadata Defaults
                const t = row.title || d.title || "";
                const a = row.author || d.author || "";
                const v = row.voice || "onyx";
                setTitle(t);
                setAuthor(a);
                setVoice(v);

                // Smart Start
                if (d.suggestions && d.suggestions.length > 0) {
                    setStartOffset(d.suggestions[0].offset);
                    setStatusW(`Loaded. Suggested: ${d.suggestions[0].label}`);
                } else {
                    setStartOffset(0);
                    setStatusW('Loaded.');
                }
                setEndOffset(d.lengthChars || 0);

                // Auto-Cover
                const userPrompt = row.coverPrompt || "";
                if (!skipCover) {
                    // Slight delay to not block UI render
                    setTimeout(() => generateCover(t, a, 0, userPrompt, row.id), 100);
                }
            } else {
                setStatusW(`Preview Failed: ${await formatError(r)}`, true);
            }
        } catch (e) {
            setStatusW(`Network Error: ${e}`, true);
        }
        setLoading(false);
    };

    // -------------------------------------------------------------------------
    // LOGIC: Cover Generation
    // -------------------------------------------------------------------------
    const COVER_STYLES = [
        "classic vintage paperback cover, elegant typography, centered composition, high detail",
        "oil painting style, rich textures, golden age illustration, masterpiece",
        "minimalist geometric symbol, modernist book cover, clean lines, bauhaus",
        "gothic engraving style, detailed line work, dark atmosphere, woodcut",
        "abstract expressionist art, bold colors, artistic interpretation, vibrant"
    ];

    const generateCover = async (t: string, a: string, styleIdx: number, userPrompt?: string, rowId?: string) => {
        const targetId = rowId || currentRow?.id;
        if (!targetId) return;

        setCoverLoading(true);
        const style = COVER_STYLES[styleIdx % COVER_STYLES.length];
        const variant = Math.floor(Math.random() * 1000);
        const base = userPrompt || style;

        // Exact Legacy Prompt
        const prompt = `${base}. Title: "${t}", Author: ${a}. Flat 2D cover (NOT a 3D book mockup). Include the exact Title + Author exactly once total. No duplicate title anywhere; no subtitle; no tagline; no extra text. No UI overlay text. [v${variant}]`;
        setCoverPrompt(prompt);

        try {
            const r = await adminFetch(`/api/library/cover/${targetId}/generate`, {
                method: 'POST', body: JSON.stringify({ prompt, title: t, author: a })
            });

            if (!r.ok) {
                setStatusW(`Cover Gen Error: ${await formatError(r)}`, true);
            } else {
                const d = await r.json();
                setCoverImg(`${d.coverUrl}?ts=${Date.now()}`); // Cache bust
            }
        } catch (e) {
            setStatusW(`Cover Network Error: ${e}`, true);
        }
        setCoverLoading(false);
    };

    const handleRegenerateCover = () => {
        setCoverStyleIdx(s => s + 1);
        generateCover(title, author, coverStyleIdx + 1, coverPrompt);
    };

    // -------------------------------------------------------------------------
    // LOGIC: Text Markers & Viewport
    // -------------------------------------------------------------------------
    const MARKER_START_TXT = "\n\n=== [START] ===\n\n";
    const MARKER_END_TXT = "\n\n=== [END] ===\n\n";

    const getViewportState = () => {
        if (!preview) return null;
        const isTail = textTab === 'tail';
        const txt = isTail ? (preview.textTail || "") : (preview.textHead || "");
        const docLen = preview.lengthChars || 0;
        const headStart = preview.textHeadStart || 0;
        const viewportStart = isTail ? (docLen - (preview.textTail?.length || 0)) : headStart;

        let displayTxt = txt;
        let startMarkerPos = -1;
        let endMarkerPos = -1;

        // Relative offsets
        const relStart = startOffset - viewportStart;
        const relEnd = endOffset - viewportStart;

        // Insert START
        if (relStart >= 0 && relStart <= txt.length) {
            startMarkerPos = relStart;
            displayTxt = displayTxt.slice(0, relStart) + MARKER_START_TXT + displayTxt.slice(relStart);
        }

        // Insert END
        if (relEnd >= 0 && relEnd <= txt.length) {
            let insertAt = relEnd;
            if (startMarkerPos !== -1 && relEnd >= relStart) {
                insertAt += MARKER_START_TXT.length; // Shift if after start marker
            }
            endMarkerPos = insertAt;
            displayTxt = displayTxt.slice(0, insertAt) + MARKER_END_TXT + displayTxt.slice(insertAt);
        }

        return { displayTxt, viewportStart, startMarkerPos, endMarkerPos };
    };

    const handleSetOffsetFromSelection = () => {
        if (!textAreaRef.current || !preview) return;
        const selStart = textAreaRef.current.selectionStart;

        const state = getViewportState();
        if (!state) return;
        const { viewportStart, startMarkerPos, endMarkerPos } = state;

        let trueRel = selStart;

        // Adjust for markers
        if (startMarkerPos !== -1 && selStart > startMarkerPos) trueRel -= MARKER_START_TXT.length;
        if (endMarkerPos !== -1 && selStart > endMarkerPos) trueRel -= MARKER_END_TXT.length;

        const absOffset = viewportStart + trueRel;

        if (trimMode === 'start') {
            setStartOffset(absOffset);
            setStatusW(`Start set to ${absOffset}`);
        } else {
            setEndOffset(absOffset);
            setStatusW(`End set to ${absOffset}`);
        }
    };

    const adjustOffset = (delta: number) => {
        if (trimMode === 'start') setStartOffset(s => Math.max(0, s + delta));
        else setEndOffset(s => Math.max(0, Math.min(preview?.lengthChars || 0, s + delta)));
    };

    // -------------------------------------------------------------------------
    // LOGIC: Cooking
    // -------------------------------------------------------------------------
    const handleApproveAndCook = async () => {
        if (!currentRow) return;
        if (currentRow._blocked) {
            setStatusW(`Row Blocked: ${currentRow._errors?.join(', ')}`, true);
            return;
        }

        setLoading(true);
        setStatusW("Cooking...", false);
        setLastError('');

        const payload = {
            id: currentRow.id,
            sourceUrl: currentRow.sourceUrl,
            mode: 'full',
            voice: voice,
            title: title,
            author: author,
            startOffset: startOffset,
            endOffset: endOffset > 0 ? endOffset : undefined,
            coverPrompt: coverPrompt,
            coverUrl: coverImg, // Pass generated cover url if any
            resolution: 'replace', // DEFAULT: Always try to replace/update in Factory V3
            ttsModel: currentRow.ttsModel || currentRow.ttsmodel,
            instructionsProfile: currentRow.instructionsProfile || currentRow.instructionsprofile,
            narratorGender: currentRow.narratorGender || currentRow.narratorgender,
            audience: currentRow.audience
        };

        // VOICE SAFETY: Strict ID check only. No name mapping.
        // If voice is valid ID (onyx, alloy...), pass it.
        // If not (e.g. "Frederick Douglass"), default to 'onyx'. 
        // User must fix in CSV or UI dropdown if they want specific valid voice.
        const VALID_VOICES = ['onyx', 'alloy', 'echo', 'fable', 'nova', 'shimmer', 'cedar', 'marin', 'custom'];
        const safeVoice = VALID_VOICES.includes(voice) ? voice : 'onyx';

        if (safeVoice !== voice) {
            console.warn(`[Batch] Invalid voice "${voice}" -> defaulting to "${safeVoice}"`);
            payload.voice = safeVoice;
        }

        try {
            // Attempt 1: Standard Cook (with replace intent)
            let r = await adminFetch('/api/factory/cook', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            if (r.status === 409) {
                // Should not happen if we send resolution: replace, but JUST IN CASE logic shifts:
                setStatusW("Job exists. Retrying with FORCE REPLACE...", true);

                // Retry with explicit replace (redundant if above works, but safe)
                r = await adminFetch('/api/factory/cook', {
                    method: 'POST',
                    body: JSON.stringify({ ...payload, resolution: 'replace' })
                });
            }

            if (r.ok) {
                const d = await r.json();
                const msg = d.message === "Job replaced/re-queued" ? "Replaced existing job" : "Job Created";
                setStatusW(`Success: ${msg} (ID: ${d.id})`, false);

                // R2: Local Cache
                try {
                    const cache = JSON.parse(localStorage.getItem('factory_v3_recent_subs') || '[]');
                    // We might not have book title/author here if it was just CSV ID, but we try rows
                    const newItem = {
                        jobId: d.id,
                        bookId: currentRow.id,
                        title: title || currentRow.title || currentRow.id,
                        author: author || currentRow.author || 'Unknown',
                        ts: Date.now()
                    };
                    cache.unshift(newItem);
                    localStorage.setItem('factory_v3_recent_subs', JSON.stringify(cache));
                } catch (e) { console.warn("Local storage write fail", e); }

                // Advance to next row after short delay
                setTimeout(() => {
                    if (currentIndex < parsedRows.length - 1) {
                        setCurrentIndex(prev => prev + 1);
                    }
                }, 1000);
            } else {
                setStatusW(`Cook Failed: ${await formatError(r)}`, true);
            }
        } catch (e) {
            setStatusW(`Error: ${e}`, true);
        }
        setLoading(false);
    };


    // -------------------------------------------------------------------------
    // RENDER
    // -------------------------------------------------------------------------
    const state = getViewportState();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#eee', background: '#111' }}>
            {/* TOP: CSV Input */}
            <div style={{ flexShrink: 0, padding: '10px', borderBottom: '1px solid #333', background: '#222' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', alignItems: 'center' }} onClick={() => setCsvCollapsed(!csvCollapsed)}>
                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                        BATCH IMPORT <span style={{ color: '#aaa', fontWeight: 'normal', marginLeft: '10px' }}>
                            {parsedRows.length} Rows
                            (<span style={{ color: '#4caf50' }}>{counts.ready} Ready</span> | <span style={{ color: '#ef5350' }}>{counts.blocked} Blocked</span>)
                            &mdash; {currentRow?.id || 'No Selection'}
                        </span>
                    </div>
                    <div>
                        {lastError && <span style={{ color: '#ff8a80', marginRight: '15px', fontWeight: 'bold' }}>!! {lastError}</span>}
                        <button onClick={(e) => { e.stopPropagation(); handleCopyTSV(); }} style={{ marginRight: '10px', background: '#333', color: '#ccc', border: '1px solid #555', cursor: 'pointer', fontSize: '10px' }}>Copy TSV</button>
                        <span>{csvCollapsed ? 'Expand CSV v' : '^ Collapse'}</span>
                    </div>
                </div>

                {/* Stale Consumer Hint (Part 2B) */}
                {isStale && (
                    <div style={{ background: '#b71c1c', color: '#fff', fontSize: '11px', padding: '4px 10px', textAlign: 'center', fontWeight: 'bold' }}>
                        ?? Consumer is idle. Use Advanced → Run Next Once (in Dashboard) to process jobs.
                    </div>
                )}

                {!csvCollapsed && (
                    <div style={{ display: 'flex', gap: '20px', marginTop: '10px', height: '120px' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <textarea
                                value={csv}
                                onChange={e => setCsv(e.target.value)}
                                placeholder="Paste CSV/TSV..."
                                style={{ flex: 1, background: '#1a1a1a', color: '#ccc', border: '1px solid #444', fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'pre' }}
                            />
                        </div>
                        <div style={{ width: '450px', overflowY: 'auto', border: '1px solid #444', background: '#1a1a1a' }}>
                            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #555', background: '#333' }}>
                                        <th style={{ padding: '4px' }}>Status</th>
                                        <th style={{ padding: '4px' }}>ID</th>
                                        <th style={{ padding: '4px' }}>Voice</th>
                                        <th style={{ padding: '4px' }}>Title</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parsedRows.map((r, i) => (
                                        <tr
                                            key={i}
                                            onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                                            style={{
                                                cursor: 'pointer',
                                                background: currentIndex === i ? '#2e7d32' : (r._blocked ? '#300' : 'transparent'),
                                                color: currentIndex === i ? '#fff' : (r._blocked ? '#ff8a80' : '#aaa')
                                            }}
                                        >
                                            <td style={{ padding: '2px 4px' }}>
                                                {r._blocked ? '?? BLOCK' : (r._voiceDefaulted ? '⚠ DFLT' : 'OK')}
                                            </td>
                                            <td style={{ padding: '2px 4px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.id}</td>
                                            <td style={{ padding: '2px 4px', maxWidth: '60px' }}>{r.voice}</td>
                                            <td style={{ padding: '2px 4px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* MAIN: 3-Panel Fixed Layout */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

                {/* LEFT: Text Source (Fluid) */}
                <div style={{ flex: 1, borderRight: '1px solid #333', display: 'flex', flexDirection: 'column', background: '#161616', minWidth: '300px' }}>

                    {/* Toolbar */}
                    <div style={{ padding: '8px', borderBottom: '1px solid #333', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden' }}>
                            <button onClick={() => setTextTab('head')} style={{ background: textTab === 'head' ? '#444' : '#222', color: 'white', border: 'none', padding: '6px 10px', cursor: 'pointer' }}>Head</button>
                            <button onClick={() => setTextTab('tail')} style={{ background: textTab === 'tail' ? '#444' : '#222', color: 'white', border: 'none', padding: '6px 10px', cursor: 'pointer' }}>Tail</button>
                        </div>

                        <div style={{ width: '1px', background: '#444', height: '20px' }}></div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                                type="text"
                                value={previewWindowStart}
                                onChange={e => setPreviewWindowStart(e.target.value)}
                                placeholder="Ofst"
                                style={{ width: '60px', background: '#000', border: '1px solid #444', color: '#fff', padding: '4px', fontSize: '11px' }}
                            />
                            <button onClick={reloadPreviewWindow} style={{ background: '#37474f', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '2px', cursor: 'pointer', fontSize: '11px' }}>
                                GO
                            </button>
                        </div>

                        <div style={{ flex: 1 }}></div>

                        {/* Quick Jumps */}
                        {preview?.suggestions?.map((s: any, i: number) => (
                            <button
                                key={i}
                                onClick={() => setStartOffset(s.offset)}
                                style={{ background: '#006064', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '2px', cursor: 'pointer', fontSize: '10px' }}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>

                    <div style={{ padding: '8px', borderBottom: '1px solid #333', display: 'flex', gap: '10px', alignItems: 'center', background: '#000' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11px', color: '#888' }}>START:</span>
                            <input
                                type="number"
                                value={startOffset}
                                onChange={e => setStartOffset(parseInt(e.target.value))}
                                style={{ width: '80px', background: trimMode === 'start' ? '#333' : '#111', border: trimMode === 'start' ? '1px solid #d32f2f' : '1px solid #333', color: '#fff', padding: '4px', fontWeight: 'bold' }}
                                onFocus={() => setTrimMode('start')}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '11px', color: '#888' }}>END:</span>
                            <input
                                type="number"
                                value={endOffset}
                                onChange={e => setEndOffset(parseInt(e.target.value))}
                                style={{ width: '80px', background: trimMode === 'end' ? '#333' : '#111', border: trimMode === 'end' ? '1px solid #d32f2f' : '1px solid #333', color: '#fff', padding: '4px' }}
                                onFocus={() => setTrimMode('end')}
                            />
                        </div>

                        <button onClick={() => adjustOffset(-50)} style={{ background: '#333', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '2px', cursor: 'pointer' }}>&lt;</button>
                        <button onClick={() => adjustOffset(+50)} style={{ background: '#333', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '2px', cursor: 'pointer' }}>&gt;</button>

                        <div style={{ flex: 1 }}></div>

                        <Tooltip content="Insert marker at cursor position" side="left">
                            <button onClick={handleSetOffsetFromSelection} style={{ background: '#d32f2f', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                                MARK {trimMode.toUpperCase()}
                            </button>
                        </Tooltip>
                    </div>

                    {/* Editor */}
                    <div style={{ flex: 1, position: 'relative' }}>
                        {state ? (
                            <textarea
                                ref={textAreaRef}
                                value={state.displayTxt}
                                readOnly
                                style={{
                                    width: '100%', height: '100%',
                                    background: '#080808', color: '#ccc',
                                    border: 'none', fontFamily: 'monospace', fontSize: '12px',
                                    padding: '10px', resize: 'none', outline: 'none'
                                }}
                            />
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#444', fontStyle: 'italic' }}>
                                {loading ? 'Loading Text Source...' : 'Select a row to load source text.'}
                            </div>
                        )}
                    </div>
                </div>

                {/* MIDDLE: Controls (Fixed Width) */}
                <div style={{ width: '350px', borderRight: '1px solid #333', display: 'flex', flexDirection: 'column', background: '#161616' }}>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                        <div style={{ background: '#222', padding: '10px', borderRadius: '4px' }}>
                            <div style={{ fontSize: '10px', color: '#666', marginBottom: '5px' }}>TARGET</div>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#eee' }}>{currentRow?.id || '-'}</div>
                            <div style={{ fontSize: '11px', color: '#aaa', wordBreak: 'break-all' }}>{currentRow?.sourceUrl}</div>
                        </div>

                        {/* Metadata Inputs */}
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>TITLE</label>
                            <input type="text" value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', padding: '8px', background: '#222', border: '1px solid #444', color: '#eee' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>AUTHOR</label>
                            <input type="text" value={author} onChange={e => setAuthor(e.target.value)} style={{ width: '100%', padding: '8px', background: '#222', border: '1px solid #444', color: '#eee' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '11px', color: '#888', marginBottom: '4px' }}>VOICE</label>
                            <input type="text" value={voice} onChange={e => setVoice(e.target.value)} list="voice-options" style={{ width: '100%', padding: '8px', background: '#222', border: '1px solid #444', color: '#eee' }} />
                            <datalist id="voice-options">
                                <option value="onyx" />
                                <option value="alloy" />
                                <option value="echo" />
                                <option value="fable" />
                                <option value="nova" />
                                <option value="shimmer" />
                            </datalist>
                        </div>

                        {/* Status Box */}
                        <div style={{ padding: '10px', background: '#222', borderRadius: '4px', fontSize: '11px', color: '#aaa', minHeight: '60px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#fff' }}>STATUS LOG</div>
                            {status}
                        </div>
                    </div>

                    {/* Action Footer */}
                    <div style={{ padding: '20px', borderTop: '1px solid #333', background: '#111' }}>
                        <button
                            onClick={handleApproveAndCook}
                            disabled={loading || !currentRow}
                            style={{
                                width: '100%',
                                padding: '16px',
                                background: loading ? '#333' : '#2e7d32',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: loading ? 'default' : 'pointer',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
                            }}
                        >
                            {loading ? 'PROCESSING...' : 'APPROVE & COOK >>'}
                        </button>
                    </div>
                </div>

                {/* RIGHT: Cover & Preview (Fixed Width) */}
                <div style={{ width: '280px', padding: '15px', background: '#0e0e0e', display: 'flex', flexDirection: 'column', gap: '20px', borderLeft: '1px solid #333' }}>

                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#888', borderBottom: '1px solid #333', paddingBottom: '5px' }}>ARTWORK PREVIEW</div>

                    {/* Cover Preview */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ width: '100%', aspectRatio: '2/3', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', border: '1px solid #333', overflow: 'hidden' }}>
                            {coverLoading ? <span style={{ fontSize: '11px', color: '#666' }}>Generating...</span> :
                                coverImg ? <img src={coverImg} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> :
                                    <span style={{ fontSize: '11px', color: '#444' }}>No Cover</span>
                            }
                        </div>
                        <button onClick={handleRegenerateCover} disabled={coverLoading || !currentRow} style={{ width: '100%', background: '#333', color: '#fff', border: '1px solid #444', padding: '6px', borderRadius: '2px', cursor: 'pointer', fontSize: '11px' }}>
                            REGENERATE ART
                        </button>
                    </div>

                    {/* Prompt Editor */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <label style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>PROMPT OVERRIDE</label>
                        <textarea
                            value={coverPrompt}
                            onChange={e => setCoverPrompt(e.target.value)}
                            style={{ flex: 1, background: '#1a1a1a', color: '#eee', border: '1px solid #444', fontSize: '10px', padding: '6px', resize: 'none' }}
                        />
                    </div>

                    {/* Pipeline Info */}
                    <div style={{ fontSize: '11px', color: '#666', padding: '10px', background: '#161616', borderRadius: '4px' }}>
                        <div style={{ marginBottom: '2px' }}>Pipeline Checklist:</div>
                        <div style={{ color: startOffset > 0 ? '#81c784' : '#e57373' }}>• Offset: {startOffset}</div>
                        <div style={{ color: voice ? '#81c784' : '#e57373' }}>• Voice: {voice}</div>
                        <div style={{ color: coverImg ? '#81c784' : '#e57373' }}>• Cover: {coverImg ? 'OK' : 'Pending'}</div>
                    </div>

                </div>

            </div>
        </div>
    );
};

export const BatchImportView: React.FC<{ diagnostics: QueueDiagnostics | null }> = ({ diagnostics }) => {
    const [mode, setMode] = useState<'zip' | 'csv'>('zip');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #333', background: '#222' }}>
                <button
                    onClick={() => setMode('zip')}
                    style={{
                        padding: '10px 20px',
                        background: mode === 'zip' ? '#111' : 'transparent',
                        color: mode === 'zip' ? '#fff' : '#888',
                        border: 'none', borderRight: '1px solid #333',
                        cursor: 'pointer', fontWeight: 'bold'
                    }}
                >
                    AUDIO ZIP IMPORT
                </button>
                <button
                    onClick={() => setMode('csv')}
                    style={{
                        padding: '10px 20px',
                        background: mode === 'csv' ? '#111' : 'transparent',
                        color: mode === 'csv' ? '#fff' : '#888',
                        border: 'none', borderRight: '1px solid #333',
                        cursor: 'pointer', fontWeight: 'bold'
                    }}
                >
                    TEXT BATCH IMPORT (CSV)
                </button>
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
                {mode === 'zip' ? <BatchZipImport /> : <BatchCsvImport diagnostics={diagnostics} />}
            </div>
        </div>
    );
};
